import { useState, useRef, useCallback, useEffect } from "react";
import type {
  DemoFlow,
  DemoFlowChoiceStep,
  DemoFlowCtaStep,
  DemoFlowActionSetAction,
} from "./flowTypes";
import type { CtaDestination, CtaIntent } from "./sectorConfigs";
import { CTA_INTENT_CONFIG } from "./sectorConfigs";
import type { DemoFormData, Campaign } from "./demoTypes";
import {
  SIGNAL_POINTS,
  SIGNAL_EVENT_LABEL,
  SIGNAL_COPY,
  intentLevelFor,
  type SignalEventType,
  type SignalEventMeta,
  type DemoSignalState,
  type SignalCardData,
} from "./demoSignal";
import { trackDemoEvent, trackDemoComplete } from "./smartDemoTracking";

export interface ChatMessage {
  id: string;
  sender: "customer" | "business";
  type:
    | "text"
    | "image"
    | "video"
    | "branded_welcome"
    | "gallery"
    | "campaign_list"
    | "dual_cta";
  content?: string;
  mediaIndex?: number;
  title?: string;
  categoryName?: string;
  mediaUrls?: string[];
  campaigns?: Campaign[];
  actions?: DemoFlowActionSetAction[];
}

export type InputMode = "start" | "question" | "disabled";

/* Sabit, deterministik zamanlama — Math.random YOK */
const TYPING_MS = 500;
const STEP_PAUSE_MS = 650;
const ANSWER_PAUSE_MS = 450;
const START_DELAY_MS = 400;
const CARD_EXIT_MS = 300;
const PULSE_MS = 900;
const EXPLAINER_AFTER = 3;

export function useDemoSimulator(flow: DemoFlow, data: DemoFormData) {
  const [started, setStarted] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [selectedChoices, setSelectedChoices] = useState<Record<string, string>>({});
  const [isTyping, setIsTyping] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [inputMode, setInputMode] = useState<InputMode>("start");
  const [currentChoice, setCurrentChoice] = useState<DemoFlowChoiceStep | null>(null);
  const [finalCta, setFinalCta] = useState<DemoFlowCtaStep | null>(null);
  const [finalActions, setFinalActions] = useState<DemoFlowActionSetAction[] | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const [awaitingContinue, setAwaitingContinue] = useState(false);
  const [handoffAction, setHandoffAction] = useState<DemoFlowActionSetAction | null>(null);
  const [handoffDone, setHandoffDone] = useState(false);

  /* Demo davranış sinyali — client-only, gerçek Meta verisi DEĞİL */
  const [signal, setSignal] = useState<DemoSignalState>({ score: 0, events: [] });
  const [signalCard, setSignalCard] = useState<SignalCardData | null>(null);
  const [signalLeaving, setSignalLeaving] = useState(false);
  const [scorePulse, setScorePulse] = useState(false);
  const [showExplainer, setShowExplainer] = useState(false);

  const idRef = useRef(0);
  const stepRef = useRef(0);
  const timersRef = useRef<number[]>([]);
  const cardTimerRef = useRef<number | null>(null);
  const pulseTimerRef = useRef<number | null>(null);
  const eventCountRef = useRef(0);
  const signalCountRef = useRef(0);
  const explainerShownRef = useRef(false);
  const ctaClickedRef = useRef(false);
  const scoreRef = useRef(0);
  const levelRef = useRef(intentLevelFor(0));
  const showingRef = useRef(false);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach((t) => window.clearTimeout(t));
    timersRef.current = [];
  }, []);

  const schedule = useCallback((ms: number, fn: () => void) => {
    const t = window.setTimeout(fn, ms);
    timersRef.current.push(t);
  }, []);

  const push = useCallback((msg: Omit<ChatMessage, "id">) => {
    idRef.current += 1;
    const full: ChatMessage = { ...msg, id: `m-${idRef.current}` };
    setMessages((prev) => [...prev, full]);
  }, []);

  /* Sinyal kartı — tek kart aynı anda görünür ve kalıcıdır (timer ile kaybolmaz).
     Yeni sinyal geldiğinde mevcut kart kısa exit animasyonuyla çıkar, yenisi girer. */
  const showCard = useCallback((card: SignalCardData) => {
    showingRef.current = true;
    setSignalLeaving(false);
    setSignalCard(card);
  }, []);

  const enqueueCard = useCallback(
    (card: SignalCardData) => {
      if (!showingRef.current) {
        showCard(card);
        return;
      }
      // Kart zaten görünüyor: exit animasyonu, ardından yeni kart kalıcı gelir.
      setSignalLeaving(true);
      if (cardTimerRef.current) window.clearTimeout(cardTimerRef.current);
      cardTimerRef.current = window.setTimeout(() => showCard(card), CARD_EXIT_MS);
    },
    [showCard],
  );

  const pulseScore = useCallback(() => {
    setScorePulse(true);
    if (pulseTimerRef.current) window.clearTimeout(pulseTimerRef.current);
    pulseTimerRef.current = window.setTimeout(() => setScorePulse(false), PULSE_MS);
  }, []);

  const emit = useCallback(
    (
      type: SignalEventType,
      opts?: { meta?: SignalEventMeta; title?: string; description?: string; points?: number },
    ) => {
      const points = opts?.points ?? SIGNAL_POINTS[type];
      eventCountRef.current += 1;
      const evId = eventCountRef.current;

      scoreRef.current += points;
      setSignal((s) => ({
        score: scoreRef.current,
        events: [
          ...s.events,
          { id: `ev-${evId}`, type, label: SIGNAL_EVENT_LABEL[type], points, meta: opts?.meta },
        ],
      }));

      // demo_started skora işlenir ama kart/pulse göstermez (başlangıç örtülü).
      if (type !== "demo_started") {
        signalCountRef.current += 1;
        const copy = SIGNAL_COPY[type];
        enqueueCard({
          id: evId,
          kind: "signal",
          title: opts?.title ?? copy.title,
          description: opts?.description ?? copy.description,
          points,
        });
        pulseScore();
      }

      // Potansiyel seviyesi değiştiyse ayrı mini geçiş kartı (yalnız seviye değişiminde).
      const newLevel = intentLevelFor(scoreRef.current);
      if (newLevel !== levelRef.current) {
        levelRef.current = newLevel;
        enqueueCard({
          id: evId + 100000,
          kind: "levelup",
          title: "Potansiyel seviyesi yükseldi",
          description: newLevel,
          points: 0,
        });
      }

      // En az 3 anlamlı event sonrası açıklayıcı bir kez gösterilir (erken kesmez).
      if (!explainerShownRef.current && signalCountRef.current >= EXPLAINER_AFTER) {
        explainerShownRef.current = true;
        setShowExplainer(true);
      }
    },
    [enqueueCard, pulseScore],
  );

  const playStep = useCallback(
    (index: number) => {
      if (index >= flow.steps.length) {
        setCompleted(true);
        setInputMode("disabled");
        setCurrentChoice(null);
        return;
      }

      stepRef.current = index;
      setCurrentStepIndex(index);
      const step = flow.steps[index];
      setCurrentChoice(null);
      setHint(null);

      if (step.type === "welcome" || step.type === "text" || step.type === "question") {
        setIsTyping(true);
        schedule(TYPING_MS, () => {
          setIsTyping(false);
          // Logo varsa, welcome tek bir branded karşılama kartı olarak render edilir (branding, signal değil).
          if (step.type === "welcome" && data.logo) {
            push({ sender: "business", type: "branded_welcome", content: step.message });
            setInputMode("disabled");
            schedule(STEP_PAUSE_MS, () => playStep(index + 1));
            return;
          }
          push({ sender: "business", type: "text", content: step.message });
          if (step.type === "text" && step.id === "campaign") {
            emit("campaign_viewed");
            trackDemoEvent("campaign:main", "campaign_viewed", { step_id: step.id });
          }
          if (step.type === "question") {
            setInputMode("question");
          } else {
            setInputMode("disabled");
            schedule(STEP_PAUSE_MS, () => playStep(index + 1));
          }
        });
      } else if (step.type === "image" || step.type === "video") {
        setIsTyping(true);
        schedule(TYPING_MS, () => {
          setIsTyping(false);
          push({
            sender: "business",
            type: step.type,
            mediaIndex: step.mediaIndex,
            content: step.caption,
          });
          setInputMode("disabled");
          // Hem görsel hem video "Devam Et" bekler (quick reply hissi).
          setAwaitingContinue(true);
        });
      } else if (step.type === "gallery") {
        setIsTyping(true);
        schedule(TYPING_MS, () => {
          setIsTyping(false);
          push({
            sender: "business",
            type: "gallery",
            title: step.title,
            categoryName: step.categoryName,
            mediaUrls: step.mediaUrls,
          });
          setInputMode("disabled");
          setAwaitingContinue(true);
        });
      } else if (step.type === "campaign_list") {
        setIsTyping(true);
        schedule(TYPING_MS, () => {
          setIsTyping(false);
          push({ sender: "business", type: "campaign_list", campaigns: step.campaigns });
          emit("campaign_viewed");
          trackDemoEvent("campaign:main", "campaign_viewed", { step_id: step.id });
          setInputMode("disabled");
          setAwaitingContinue(true);
        });
      } else if (step.type === "choice") {
        setIsTyping(true);
        schedule(TYPING_MS, () => {
          setIsTyping(false);
          push({ sender: "business", type: "text", content: step.message });
          setCurrentChoice(step);
          setInputMode("disabled");
        });
      } else if (step.type === "cta") {
        setIsTyping(true);
        schedule(TYPING_MS, () => {
          setIsTyping(false);
          if (step.message) {
            push({ sender: "business", type: "text", content: step.message });
          }
          setFinalCta(step);
          emit("cta_reached");
          trackDemoEvent("cta:reached", "cta_reached");
          setCompleted(true);
          setInputMode("disabled");
        });
      } else if (step.type === "dual_cta") {
        setIsTyping(true);
        schedule(TYPING_MS, () => {
          setIsTyping(false);
          setFinalActions(
            step.actions.map((a, i) => ({
              id: a.label,
              label: a.label,
              destination: "whatsapp" as CtaDestination,
              prefill: a.prefill,
              intent: (i === 0 ? "appointment" : "question") as CtaIntent,
            })),
          );
          emit("cta_reached");
          trackDemoEvent("cta:reached", "cta_reached");
          setCompleted(true);
          setInputMode("disabled");
        });
      } else if (step.type === "action_set") {
        setIsTyping(true);
        schedule(TYPING_MS, () => {
          setIsTyping(false);
          setFinalActions(step.actions);
          emit("cta_reached");
          trackDemoEvent("cta:reached", "cta_reached");
          setCompleted(true);
          setInputMode("disabled");
        });
      }
    },
    [flow, data, push, schedule, emit],
  );

  const start = useCallback(() => {
    setStarted(true);
    setInputMode("disabled");
    push({ sender: "customer", type: "text", content: "Merhaba" });
    emit("demo_started");
    trackDemoEvent("demo:started", "demo_started");
    setIsTyping(true);
    schedule(START_DELAY_MS, () => playStep(0));
  }, [push, schedule, playStep, emit]);

  const send = useCallback(
    (text: string) => {
      if (inputMode === "start") {
        const normalized = text.trim().toLocaleLowerCase("tr-TR").replace(/\s+/g, "");
        if (normalized === "merhaba") {
          start();
        } else {
          setHint("Denemeyi başlatmak için MERHABA yazın.");
        }
        return;
      }

      if (inputMode === "question") {
        const answer = text.trim();
        if (!answer) return;
        push({ sender: "customer", type: "text", content: answer });
        const step = flow.steps[stepRef.current];
        if (step && step.type === "question") {
          setAnswers((a) => ({ ...a, [step.id]: answer }));
          emit("question_answered");
        }
        setInputMode("disabled");
        schedule(ANSWER_PAUSE_MS, () => playStep(stepRef.current + 1));
      }
    },
    [inputMode, start, push, schedule, playStep, flow, emit],
  );

  const choose = useCallback(
    (option: string) => {
      const step = flow.steps[stepRef.current];
      if (!step || step.type !== "choice") return;
      push({ sender: "customer", type: "text", content: option });
      setSelectedChoices((c) => ({ ...c, [step.id]: option }));
      emit("service_selected");
      trackDemoEvent(`service:${option}`, "service_selected", { service: option });
      setCurrentChoice(null);
      setInputMode("disabled");
      schedule(ANSWER_PAUSE_MS, () => playStep(stepRef.current + 1));
    },
    [flow, push, schedule, playStep, emit],
  );

  const continueFlow = useCallback(() => {
    const step = flow.steps[stepRef.current];
    if (step && step.type === "image") emit("image_viewed");
    else if (step && step.type === "video") {
      emit("video_engaged");
      trackDemoEvent(`video:${step.id}`, "video_viewed", { step_id: step.id });
    } else if (step && step.type === "gallery") {
      emit("gallery_viewed", {
        meta: { categoryName: step.categoryName },
        title: `${step.categoryName} galerisi incelendi`,
      });
      trackDemoEvent(`gallery:${step.id}`, "gallery_viewed", {
        category: step.categoryName,
        step_id: step.id,
      });
    }
    // campaign_list sinyali push anında üretilir (ulaşıldı); burada ekstra emit yok.
    setAwaitingContinue(false);
    setInputMode("disabled");
    schedule(ANSWER_PAUSE_MS, () => playStep(stepRef.current + 1));
  }, [flow, schedule, playStep, emit]);

  const registerCtaClick = useCallback(() => {
    if (ctaClickedRef.current) return;
    ctaClickedRef.current = true;
    emit("cta_clicked");
  }, [emit]);

  const registerActionClick = useCallback(
    (action: DemoFlowActionSetAction) => {
      if (ctaClickedRef.current) return;
      ctaClickedRef.current = true;
      const intentConfig = CTA_INTENT_CONFIG[action.intent];
      emit("cta_intent", {
        meta: { ctaLabel: action.label, ctaId: action.id },
        title: intentConfig.signalTitle,
        description: intentConfig.signalDescription,
        points: intentConfig.score,
      });
      trackDemoEvent(`cta:intent:${action.id}`, "cta_intent", {
        cta_id: action.id,
        cta_label: action.label,
        intent: action.intent,
        score_delta: intentConfig.score,
      });
      // Kısa controlled delay: önce sinyal kartı görünür, sonra destination simülasyonu gelir.
      schedule(650, () => {
        setHandoffAction(action);
      });
    },
    [emit, schedule],
  );

  const completeHandoff = useCallback(() => {
    setHandoffDone(true);
    trackDemoComplete({
      finalScore: scoreRef.current,
      finalIntent: handoffAction?.intent ?? "",
      selectedService: selectedChoices["service-choice"] ?? null,
      selectedCtaId: handoffAction?.id ?? null,
      selectedCtaLabel: handoffAction?.label ?? null,
    });
  }, [handoffAction, selectedChoices]);

  const dismissExplainer = useCallback(() => setShowExplainer(false), []);

  const reset = useCallback(() => {
    clearTimers();
    if (cardTimerRef.current) {
      window.clearTimeout(cardTimerRef.current);
      cardTimerRef.current = null;
    }
    if (pulseTimerRef.current) {
      window.clearTimeout(pulseTimerRef.current);
      pulseTimerRef.current = null;
    }
    idRef.current = 0;
    stepRef.current = 0;
    eventCountRef.current = 0;
    signalCountRef.current = 0;
    explainerShownRef.current = false;
    ctaClickedRef.current = false;
    scoreRef.current = 0;
    levelRef.current = intentLevelFor(0);
    showingRef.current = false;
    setSignal({ score: 0, events: [] });
    setSignalCard(null);
    setSignalLeaving(false);
    setScorePulse(false);
    setShowExplainer(false);
    setStarted(false);
    setCurrentStepIndex(0);
    setMessages([]);
    setAnswers({});
    setSelectedChoices({});
    setIsTyping(false);
    setCompleted(false);
    setInputMode("start");
    setCurrentChoice(null);
    setFinalCta(null);
    setFinalActions(null);
    setHint(null);
    setAwaitingContinue(false);
    setHandoffAction(null);
    setHandoffDone(false);
  }, [clearTimers]);

  useEffect(() => {
    return () => {
      clearTimers();
      if (cardTimerRef.current) window.clearTimeout(cardTimerRef.current);
      if (pulseTimerRef.current) window.clearTimeout(pulseTimerRef.current);
    };
  }, [clearTimers]);

  /* Completion'a geçildiğinde kalıcı sinyal kartı temizlenir (ekran temiz açılır). */
  useEffect(() => {
    if (completed) {
      showingRef.current = false;
      if (cardTimerRef.current) window.clearTimeout(cardTimerRef.current);
      cardTimerRef.current = null;
      setSignalCard(null);
      setSignalLeaving(false);
    }
  }, [completed]);

  return {
    started,
    currentStepIndex,
    messages,
    answers,
    selectedChoices,
    isTyping,
    completed,
    inputMode,
    currentChoice,
    finalCta,
    finalActions,
    hint,
    awaitingContinue,
    signal,
    signalCard,
    signalLeaving,
    scorePulse,
    showExplainer,
    send,
    choose,
    continueFlow,
    registerCtaClick,
    registerActionClick,
    completeHandoff,
    handoffAction,
    handoffDone,
    dismissExplainer,
    reset,
  };
}